import React, { useState } from 'react';
import { Zap, Thermometer, Sun, Lock, Wind, ToggleLeft, Users, Power, Lightbulb } from 'lucide-react';

const BuildingAutomation: React.FC = () => {
  const [activeZones, setActiveZones] = useState<{ [key: string]: boolean }>({
    'North Wing': true,
    'South Wing': true,
    'East Wing': true,
    'West Wing': true
  });
  
  const [hvacSettings, setHvacSettings] = useState<{ [key: string]: number }>({
    'North Wing': 72,
    'South Wing': 71,
    'East Wing': 73,
    'West Wing': 72
  });

  const [lightingLevels, setLightingLevels] = useState<{ [key: string]: number }>({
    'North Wing': 80,
    'South Wing': 75,
    'East Wing': 85,
    'West Wing': 70
  });

  // Mock data
  const automationData = {
    hvacZones: [
      {
        name: 'North Wing',
        currentTemp: hvacSettings['North Wing'],
        targetTemp: hvacSettings['North Wing'],
        mode: 'Auto',
        occupancy: 85,
        energyStatus: 'Optimal',
        lightingLevel: lightingLevels['North Wing'],
        areas: ['Open Office', 'Meeting Rooms', 'Break Room']
      },
      {
        name: 'South Wing',
        currentTemp: hvacSettings['South Wing'],
        targetTemp: hvacSettings['South Wing'],
        mode: 'Auto',
        occupancy: 65,
        energyStatus: 'Optimal',
        lightingLevel: lightingLevels['South Wing'],
        areas: ['Development Area', 'Conference Rooms']
      },
      {
        name: 'East Wing',
        currentTemp: hvacSettings['East Wing'],
        targetTemp: hvacSettings['East Wing'],
        mode: 'Auto',
        occupancy: 72,
        energyStatus: 'High Usage',
        lightingLevel: lightingLevels['East Wing'],
        areas: ['Executive Offices', 'Meeting Rooms']
      },
      {
        name: 'West Wing',
        currentTemp: hvacSettings['West Wing'],
        targetTemp: hvacSettings['West Wing'],
        mode: 'Auto',
        occupancy: 45,
        energyStatus: 'Eco Mode',
        lightingLevel: lightingLevels['West Wing'],
        areas: ['Reception', 'Visitor Area']
      }
    ],
    lighting: {
      zones: ['Open Office', 'Meeting Rooms', 'Corridors', 'Reception'],
      schedule: {
        workHours: '7:00 AM - 7:00 PM',
        afterHours: 'Motion Activated'
      }
    },
    access: {
      securedZones: 12,
      activePoints: 8,
      currentStatus: 'Normal Operations',
      recentEvents: [
        { time: '10:45 AM', event: 'Door Access - Main Entrance', type: 'normal' },
        { time: '10:30 AM', event: 'Security Override - Server Room', type: 'override' },
        { time: '10:15 AM', event: 'Access Denied - R&D Lab', type: 'alert' }
      ]
    },
    automationRules: [
      {
        name: 'After Hours HVAC',
        condition: 'Occupancy < 10%',
        action: 'Set to ECO mode',
        status: 'Active'
      },
      {
        name: 'Dynamic Lighting',
        condition: 'Natural light > 80%',
        action: 'Reduce artificial lighting',
        status: 'Active'
      },
      {
        name: 'Meeting Room Climate',
        condition: 'Room booked',
        action: 'Adjust temperature 10min before',
        status: 'Active'
      }
    ]
  };

  const handleZoneToggle = (zone: string) => {
    setActiveZones(prev => ({
      ...prev,
      [zone]: !prev[zone]
    }));
  };

  const handleTempChange = (zone: string, temp: number) => {
    setHvacSettings(prev => ({
      ...prev,
      [zone]: temp
    }));
  };

  const handleLightingChange = (zone: string, level: number) => {
    setLightingLevels(prev => ({
      ...prev,
      [zone]: level
    }));
  };

  const getEnergyStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'optimal':
        return 'text-green-500';
      case 'high usage':
        return 'text-yellow-500';
      case 'eco mode':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'alert':
        return 'text-red-500';
      case 'override':
        return 'text-yellow-500';
      default:
        return 'text-green-500';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Building Automation Control Center</h2>
      
      {/* Quick Controls */}
      <div className="mb-8 bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <Power className="mr-2 text-blue-500" />
          Zone Power Management
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {automationData.hvacZones.map((zone) => (
            <div key={zone.name} className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <span className="font-medium">{zone.name}</span>
                <button
                  onClick={() => handleZoneToggle(zone.name)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    activeZones[zone.name]
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {activeZones[zone.name] ? 'Active' : 'Inactive'}
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Temperature</label>
                  <div className="flex items-center">
                    <Thermometer className="text-gray-400 mr-2" size={18} />
                    <input
                      type="range"
                      min="65"
                      max="80"
                      value={hvacSettings[zone.name]}
                      onChange={(e) => handleTempChange(zone.name, parseInt(e.target.value))}
                      className="flex-grow"
                      disabled={!activeZones[zone.name]}
                    />
                    <span className="ml-2 min-w-[3rem] text-right">{hvacSettings[zone.name]}°F</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Lighting</label>
                  <div className="flex items-center">
                    <Lightbulb className="text-gray-400 mr-2" size={18} />
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={lightingLevels[zone.name]}
                      onChange={(e) => handleLightingChange(zone.name, parseInt(e.target.value))}
                      className="flex-grow"
                      disabled={!activeZones[zone.name]}
                    />
                    <span className="ml-2 min-w-[3rem] text-right">{lightingLevels[zone.name]}%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* HVAC Zones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">HVAC Zone Status</h3>
          <div className="space-y-4">
            {automationData.hvacZones.map((zone, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{zone.name}</h4>
                    <p className="text-sm text-gray-500">Mode: {zone.mode}</p>
                  </div>
                  <span className={`text-sm font-medium ${getEnergyStatusColor(zone.energyStatus)}`}>
                    {zone.energyStatus}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center">
                    <Thermometer size={16} className="text-gray-400 mr-1" />
                    <span className="text-sm">
                      {zone.currentTemp}°F → {zone.targetTemp}°F
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Users size={16} className="text-gray-400 mr-1" />
                    <span className="text-sm">{zone.occupancy}% occupied</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Access Control</h3>
          <div className="space-y-4">
            {automationData.access.recentEvents.map((event, index) => (
              <div key={index} className="flex items-start justify-between border-b pb-4 last:border-b-0">
                <div>
                  <p className={`font-medium ${getEventTypeColor(event.type)}`}>
                    {event.event}
                  </p>
                  <p className="text-sm text-gray-500">{event.time}</p>
                </div>
                <button className="px-3 py-1 text-sm rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200">
                  Details
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Automation Rules */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Automation Rules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {automationData.automationRules.map((rule, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium">{rule.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">If: {rule.condition}</p>
                  <p className="text-sm text-gray-600">Then: {rule.action}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  rule.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {rule.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BuildingAutomation; 